import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import AsyncSelect from 'react-select/async';
import { useDebouncedEffect } from "../../hooks/useDebounceEffect";
import { lookupOptions } from "./hooks/useOptionsLookup";
import { useSideChannelSubscription } from "../../util/useSideChannel";
import getPathFromId from '../../util/getPathFromId';
import { useJnx, useMultipleJnx } from "../../util/jnx";

/**
 * Autocomplete Field Component
 *
 * An async searchable select field that fetches options from an API endpoint.
 * It show all options upfront and let you search as you type
 * ## Features
 * - Async search with debouncing (triggers after 3+ characters)
 * - automatically refetches if the lookup has the param expresion
 * - Can be programmatically set/cleared via $set() from other fields
 * - Error display and loading states
 *
 * ## Schema Configuration
 *
 * ```json
 * {
 *   "fieldName": {
 *     "type": "string",
 *     "title": "Lead Name",
 *     "ui:field": "Autocomplete",
 *     "ui:onSelect": {
 *       "expr": "$set({'otherField': value, 'anotherField': {'value': id, 'label': name, 'item': $}})",
 *       "debug": "optional-debug-label"
 *     },
 *     "lookup": {
 *       "resource": "EntityName",
 *       "api": "api:endpoint/path",
 *       "searchParam": "searchFieldName",
 *       "params": {
 *         "expr:dynamicParam": "formField.value"  // Reactive - triggers refetch on change
 *       },
 *       "id": "idFieldFromResponse",
 *       "label": "$join([first_Name ? first_Name : '', ' ', last_Name])",  // JSONata expression
 *       "jnx": "data"  // Path to array in response
 *     },
 *     "map:field": "data.fieldPath"
 *   }
 * }
 */
function Autocomplete(props) {
    const {
        schema: {
            title,
            lookup,
            type
        },
        formContext: {
            setFormDataValues,
            sideChannel
        },
        value,
        formData,
        onChange,
        disabled,
        required,
        idSchema: { $id } = {},
        uiSchema: {
            'ui:readonly': uiReadonly,
            'ui:onSelect': uiOnSelectCallbackExpr,
        },
        readonly: propReadonly
    } = props;

    const {
        onSelectExpr,
        id,
        label: labelExpr,
        searchParam,
    } = lookup || {};

    const promiseRef = useRef();
    const [error, setError] = useState(null)
    const [loadedOptions, setLoadedOptions] = useState([]);
    const [hasExecutedCallback, setHasExecutedCallback] = useState(false);
    const [defaultOptions, setDefaultOptions] = useState([]);
    const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
    const hasLoadedDefaultsRef = useRef(false);
    const lastSearchedLabelRef = useRef(null);
    const previousEvaluatedParamsRef = useRef(null);
    const previousFieldDataRef = useRef(undefined);

    const rootFormData = useSideChannelSubscription(sideChannel, 0);
    // Create JNX evaluators for expr: params (e.g., "expr:region")
    const paramsJnx = useMultipleJnx(lookup?.params);

    // Evaluate expr: params to detect when they change (e.g., region changes)
    const evaluatedParams = useMemo(() => {
        if (!paramsJnx || Object.keys(paramsJnx).length === 0) return null;

        const result = {};
        Object.entries(paramsJnx).forEach(([key, jnx]) => {
            if (jnx) {
                try {
                    result[key] = jnx.eval(rootFormData || {});
                } catch (e) {
                    setError(e?.message ? e.message : "There was an error reseting the field")
                    console.error("[AutocompleteField] Error evaluating expr param:", key, e);
                }
            }
        });

        return Object.keys(result).length > 0 ? JSON.stringify(result) : null;
    }, [paramsJnx, rootFormData]);

    // Reset the loadedOption ref and clear value when evaluated params change (e.g., region changed)
    useEffect(() => {
        if (evaluatedParams !== previousEvaluatedParamsRef.current) {
            if (previousEvaluatedParamsRef.current !== null) {
                // Only reset if not the first time (avoid double fetch on mount)
                hasLoadedDefaultsRef.current = false;
                // Clear the current selection since the params changed
                setCurrent(null);
                setLoadedOptions([]);
                setDefaultOptions([]);
            }
            previousEvaluatedParamsRef.current = evaluatedParams;
        }
    }, [evaluatedParams, $id]);

    const path = useMemo(() => getPathFromId($id), [$id]);

    const functionBinds = useMemo(() => ({ set: setFormDataValues }), [setFormDataValues]);

    const labelJnx = useJnx(labelExpr);

    const onSelectJnx = useJnx(onSelectExpr);

    // Load all options on component mount
    useEffect(() => {
        const loadDefaultOptions = async () => {
            if (!lookup || isLoadingDefaults || hasLoadedDefaultsRef.current) return;

            hasLoadedDefaultsRef.current = true;
            setIsLoadingDefaults(true);
            try {
                let options = null;
                try {
                    options = await lookupOptions({ ...lookup }, rootFormData);   
                } catch (error) {
                    console.error(error)
                    setError(error.message ? error.message : "There was a problem fetching your options")
                    return []
                }
                const sortedOptions = (options || []).sort((a, b) => {
                    const labelA = (labelJnx.eval(a) || '').trim() || 'No found';
                    const labelB = (labelJnx.eval(b) || '').trim() || 'No found';
                    return labelA.localeCompare(labelB);
                });

                const formattedOptions = sortedOptions.map((obj) => ({
                    value: obj[id],
                    label: labelJnx.eval(obj),
                    item: obj
                }));

                setDefaultOptions(formattedOptions);
                setLoadedOptions(sortedOptions);
            } catch (error) {
                setError(error.message ? error.message : "There was a problem loading your options")
                console.error('[AutocompleteField] Error loading default options:', error);
            } finally {
                setIsLoadingDefaults(false);
            }
        };

        loadDefaultOptions();
    }, [lookup, id, labelJnx, rootFormData]);
    const label = useMemo(() => {
        const result = onSelectJnx?.eval(
            rootFormData || {}, path, {
            formData, rootFormData
        }
        );
        return result;
    }, [onSelectJnx, formData, rootFormData, path]);

    const uiOnSelectCallbackJnx = useJnx(uiOnSelectCallbackExpr, { functionBinds });

    const [current, setCurrent] = useState(value);
    useDebouncedEffect(() => {
        if (current !== value) {
            onChange(current?.value !== undefined ? current.value : current)
        }
    }, [current], 450);

    useEffect(() => {
        if (!value) {
            setCurrent(null);
            setHasExecutedCallback(false);
            return;
        }

        if (typeof value === 'object' && value?.value !== undefined && value?.label !== undefined) {
            setCurrent(value);
            if (value.item) {
                // Add item to loadedOptions only if it doesn't exist, don't replace all options
                setLoadedOptions(prevOptions => {
                    const itemExists = prevOptions.some(option => option[id] === value.item[id]);
                    if (itemExists) {
                        return prevOptions;
                    }
                    return [...prevOptions, value.item];
                });
            }
            setHasExecutedCallback(false);
        } else {
            setCurrent(value);
            setHasExecutedCallback(false);
        }
    }, [value, id]);

    // Handle cross-field updates via $set()
    // Only react when fieldData actually changes(e.g when it's called via $set), not on every re-render
    useEffect(() => {
        const formFieldPath = $id.replace('root_', '').replace(/_/g, '.');
        const fieldData = formFieldPath.split('.').reduce((obj, key) => obj?.[key], rootFormData);

        // Compare with previous value to detect actual changes
        const previousFieldData = previousFieldDataRef.current;
        const currentSerialized = JSON.stringify(fieldData);
        const previousSerialized = JSON.stringify(previousFieldData);

        // Skip if no change detected
        if (currentSerialized === previousSerialized) {
            return;
        }

        // Update the ref with current value
        previousFieldDataRef.current = fieldData;

        // Allow clearing the value via $set when explicitly set to null
        // Only clear if it was an actual change from a non-null value to null
        if (fieldData === null && previousFieldData !== undefined) {
            setCurrent(null);
            return;
        }

        // if the field data is an object with a value and label and item property, set the current value to the field data
        if (fieldData &&
            typeof fieldData === 'object' &&
            fieldData.value &&
            fieldData.label &&
            fieldData.item) {

            setCurrent(fieldData);
            // Add the item to loadedOptions only if it doesn't already exist
            // Don't replace all options - that breaks subsequent selections
            setLoadedOptions(prevOptions => {
                const itemExists = prevOptions.some(option => option[id] === fieldData.item[id]);
                if (itemExists) {
                    return prevOptions; // Item already in list, no change needed
                }
                return [...prevOptions, fieldData.item]; // Add new item to existing options
            });
            setHasExecutedCallback(false);
        }
    }, [rootFormData, $id, id]);

    const readonly = uiReadonly || propReadonly;

    const rootFormDataRef = useRef(rootFormData);
    rootFormDataRef.current = rootFormData;

    const promiseOptions = useCallback((inputValue, isQR = false) => {
        const searchValue = inputValue.trim();
        if (searchValue.length > 2) {
            const search = { searchParam };
            search.promise = new Promise((resolve) => {
                search.resolve = resolve;
            });
            promiseRef.current = search.promise;
            setTimeout(async () => {
                if (promiseRef.current === search.promise) {
                    let options = null
                    try {
                        options = await lookupOptions({ ...lookup, params: { ...lookup?.params, [searchParam]: searchValue } },         
                            rootFormDataRef.current);  
                        setError(null)
                    } catch (error) {
                        setError(error.message ? error.message : "There was a problem loading your options")
                        search.resolve([])
                        return ;
                    }

                    // Sort options alphabetically
                    const sortedOptions = (options || []).sort((a, b) => {
                        const labelA = (labelJnx.eval(a) || '').trim() || '';
                        const labelB = (labelJnx.eval(b) || '').trim() || '';
                        return labelA.localeCompare(labelB);
                    });

                    setLoadedOptions(sortedOptions);

                    if (isQR === true) {
                        const obj = sortedOptions?.[0];
                        setCurrent({ value: obj[id], label: labelJnx.eval(obj) });
                        search.resolve([{
                            value: obj[id],
                            label: labelJnx.eval(obj),
                            item: obj
                        }]);
                    } else {
                        const dataParse = sortedOptions.map((obj) => {
                            return {
                                value: obj[id],
                                label: labelJnx.eval(obj),
                                item: obj
                            };
                        });
                        search.resolve(dataParse);
                    }
                }
            }, 300);
            return search.promise;
        }
        return Promise.resolve(defaultOptions);
    }, [searchParam, lookup, id, labelJnx, defaultOptions]);

    // Trigger search based on onSelectExpr label - but only once per unique label value
    useEffect(() => {
        if (label && label !== lastSearchedLabelRef.current) {
            lastSearchedLabelRef.current = label;
            promiseOptions(label, true);
        }
    }, [label, promiseOptions]);

    // Execute ui:onSelectCallback when selection changes
    useEffect(() => {
        
        // Early returns to prevent infinite loops
        if (!uiOnSelectCallbackJnx) return;
        if (!current?.value) return;
        if (!loadedOptions || !loadedOptions.length) return;
        if (hasExecutedCallback) return; // Prevent re-execution
        
        const selectedObject = loadedOptions.find(option => option[id] === current.value);
        if (!selectedObject || !path) return;

        if (current?.item) {
            setHasExecutedCallback(true);
            setTimeout(() => {
                try {
                    uiOnSelectCallbackJnx.eval(selectedObject, '', {
                        fieldPath: path,
                        object: selectedObject,
                        formData: rootFormData
                    });
                } catch (error) {
                    setError(error?.message ? error.message : "There was an error when selecting an item")
                    console.error('Error in ui:onSelect:', error);
                }
            }, 100);
        }
    }, [current, loadedOptions, uiOnSelectCallbackJnx, id, path, rootFormData, $id, hasExecutedCallback]);

    return (
        <div className="autocomplete" data-cy={$id}>
            {title.trim() ? (<label className="control-label" >
                {title}{required ? <span className="required">*</span> : null}
            </label>) : null}
            
            
            <AsyncSelect
                required={required}
                isDisabled={readonly || disabled}
                isClearable
                defaultOptions={defaultOptions}
                value={current}
                loadOptions={promiseOptions}
                loadingMessage={() => "Searching..."}
                noOptionsMessage={() =>
                    error ? `❌ ${error}` : "No result"
                }
                onChange={(value) => {
                    setHasExecutedCallback(false);
                    setCurrent(value);
                }}
            />
            {error && (
                <div style={{ color: "red" }}>
                    {error}
                </div>
            )}
        </div>
    )
}

export default Autocomplete;